/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ActionReducerMapBuilder, AsyncThunk, Draft, SerializedError } from '@reduxjs/toolkit'
import { groupBy, uniq } from 'lodash'
import {
  CompareBy,
  ComparisonColumn,
  Status,
  IFilter,
  CategoryFilter,
  IClassLine,
  IPolicy,
  ISectionFeatures,
  ISelectedClassCover,
  IComparisonOptions,
  IFeatureContents,
  ISelectionModal,
  IApprovePolicyModal,
  IBuyGroup,
} from '../../types'
import { IComparisonState, initialState } from './state'
import {
  fetchClassesAsync,
  fetchPoliciesAsync,
  fetchFeaturesAsync,
  fetchComparisonOptionsAsync,
  fetchPolicyContentByIdAsync,
  fetchSectionContentByIdAsync,
  saveComparisonsAsync,
  getSavedComparisonsAsync,
  getSavedComparisonAsync,
  removeSavedComparisonAsync,
  updateSavedComparisonAsync,
  emailSavedComparisonAsync,
  fetchBuyerGroupsAsync,
  approvePolicyAsync,
  fetchInsurersAsync,
} from './thunks'

type ComparisonDraft = Draft<IComparisonState>

/**
 * Every request in this slice reports through the same two fields, so the pending and
 * rejected halves are identical for all of them and only the success step differs. Writing
 * the triple out by hand for each thunk is what let five unrelated failures end up
 * reporting "Failed to fetch saved comparisons"; going through here makes the message a
 * required argument instead of something copied from the case above.
 */
const statusCases = <Returned>(
  builder: ActionReducerMapBuilder<IComparisonState>,
  thunk: AsyncThunk<Returned, any, any>,
  error: string,
  onSuccess?: (state: ComparisonDraft, payload: Returned) => void,
  onSettled?: (state: ComparisonDraft, arg: any) => void,
) => {
  builder
    .addCase(thunk.pending, (state) => {
      state.comparisonStatus = Status.LOADING
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state.comparisonStatus = Status.IDLE
      onSuccess?.(state, action.payload as Returned)
      onSettled?.(state, action.meta?.arg)
    })
    .addCase(thunk.rejected, (state, action) => {
      state.comparisonStatus = Status.ERROR
      // The thunk is taken at its loosest signature so every one of them fits, which
      // leaves `error` untyped on the way in.
      state.comparisonError = (action.error as SerializedError)?.message || error
      // Whatever the request was holding open has to be released on the way out too, or a
      // failed fetch leaves its column saying "loading" for the rest of the session.
      onSettled?.(state, action.meta?.arg)
    })
}

/**
 * Rebuilds the whole comparison from a saved one. Which half of the payload is read
 * depends on the mode it was saved in: a policy comparison restores columns, a feature
 * comparison restores rows, and the filters are keyed to match whichever it is.
 */
const applySavedComparison = (state: ComparisonDraft, payload: any) => {
  const { mode, effective_date, feature_groups, policies, covers, findings, filter } = payload
  state.filter.compareBy = mode === 'policy' ? CompareBy.COMPANY : CompareBy.FEATURE
  state.filter.effectiveDate = effective_date
  state.filter.category = filter

  // A saved comparison with no covers has no table to rebuild, and reading the first
  // cover's id off an empty list throws inside the reducer — which rejects the dispatch
  // that is meant to have applied it, leaving the caller unable to tell load from failure.
  const savedCovers = Array.isArray(covers) ? covers : []
  const allClasses = state.productClasses?.map((cl) => cl.classes).flat()
  const foundClass =
    allClasses &&
    allClasses.find((productClass) =>
      productClass.covers.some((cover) => cover.id === savedCovers[0]?.id),
    )
  const loadedSelectedClassesCover = {
    className: foundClass?.name,
    classId: foundClass?.id,
    coverId: savedCovers.map((cover) => cover.id),
    coverType: savedCovers.map((cover) => cover.name),
  }
  state.selectedProductClassCover = loadedSelectedClassesCover

  if (mode === 'policy') {
    const filterFeatures = {}
    feature_groups.forEach((cover) =>
      cover.features.forEach((feature) => {
        if (!filterFeatures[feature.section]) {
          filterFeatures[feature.section] = []
        }
        filterFeatures[feature.section].push(feature.id)
      }),
    )
    state.filter.filterFeature = filterFeatures
    const loadedSelectedPolicyContents = policies.map((policy, slot) => ({
      id: policy.id,
      slot,
      policy: policy,
      featureContents: findings.filter((finding) => finding.policy_id === policy.id),
    }))
    state.selectedPolicyContents = loadedSelectedPolicyContents
  }

  if (mode === 'feature') {
    const filterPolicies = {}
    policies.forEach((policy) => {
      if (!filterPolicies[policy.insurer.name]) {
        filterPolicies[policy.insurer.name] = []
      }
      filterPolicies[policy.insurer.name].push(policy.id)
    })
    state.filter.filterPolicy = filterPolicies
    const loadedSelectedSectionContents = feature_groups.map((feature_group) => {
      return feature_group.features.map((feature) => {
        return {
          id: feature.id,
          featureContents: findings.filter((finding) => finding.feature_id === feature.id),
        }
      })
    })
    state.selectedSectionCovers = loadedSelectedSectionContents.flat()
  }
}

export const comparisonSlice = createSlice({
  name: 'comparison',
  initialState,
  reducers: {
    showFilter: (state) => {
      state.filterOpen = true
    },
    setCompareBy: (state, { payload }: PayloadAction<CompareBy>) => {
      state.filter.compareBy = payload
    },
    updateFilter: (state, { payload }: PayloadAction<IFilter>) => {
      state.filter = payload
    },
    updateSelectionModal: (state, { payload }: PayloadAction<ISelectionModal>) => {
      state.selectionModal = payload
    },
    updateApprovePolicyModal: (state, { payload }: PayloadAction<IApprovePolicyModal>) => {
      state.approvePolicyModal = payload
    },
    selectProductClassCover: (state, { payload }: PayloadAction<ISelectedClassCover>) => {
      state.selectedProductClassCover = payload
    },
    removeProductClassCover: (state) => {
      state.selectedProductClassCover = null
    },
    selectPolicyContents: (state, { payload }) => {
      state.selectedPolicyContents = payload
    },
    removeAllSelections: (state) => {
      state.selectedSectionCovers = []
      state.selectedPolicyContents = []
      state.importingPolicyIds = []
    },
    /** Clears lists tied to the current cover so UI does not mix data while re-fetching. */
    invalidateCoverComparisonData: (state) => {
      state.comparisonPolicies = initialState.comparisonPolicies
      state.comparisonFeatures = initialState.comparisonFeatures
      state.comparisonOptions = initialState.comparisonOptions
    },
    removeSelectedPolicyCoverDetails: (state, { payload }: PayloadAction<number | string>) => {
      const filtered = state.selectedPolicyContents.filter(
        (policy: IFeatureContents) => policy.id !== payload,
      )
      state.selectedPolicyContents = filtered
      state.importingPolicyIds = state.importingPolicyIds.filter((id) => id !== payload)
    },
    /**
     * Drops only the filters that go stale when the rows change: filterFeature and
     * filterPolicy hold the user's own picks keyed by id, so after a cover change they
     * match features and policies that are no longer listed and narrow the table to
     * nothing.
     *
     * The smart filters — differences, what's new, limits only — are deliberately left
     * alone. They are computed against whatever rows are on screen and are never written
     * back into filterFeature (see getExportFeatureIds), so they carry over to a new set of
     * covers meaning exactly what they meant before. Search and category re-apply the same
     * way and stay too.
     *
     * This is all a cover change needs: the rows underneath change, the comparison does
     * not. Clearing the columns is resetComparison's job, for a class change where the
     * selected wordings no longer belong.
     */
    clearFilterSelections: (state) => {
      state.filter = {
        ...state.filter,
        filterFeature: {},
        filterPolicy: {},
      }
    },
    resetComparison: (state) => {
      state.filter = {
        ...state.filter,
        category: CategoryFilter.ALL,
        searchFeature: '',
        filterFeature: {},
        filterPolicy: {},
        showDifferences: false,
        onlyWhatsNew: false,
        showLimitsOnly: false,
      }
      state.selectedPolicyContents = initialState.selectedPolicyContents
      state.selectedSectionCovers = initialState.selectedSectionCovers
      state.importingPolicyIds = []
      state.comparisonFeatures = initialState.comparisonFeatures
      state.comparisonOptions = initialState.comparisonOptions
      state.comparisonPolicies = initialState.comparisonPolicies
    },
    updateSelectedSectionCovers: (state, { payload }: PayloadAction<IFeatureContents[]>) => {
      state.selectedSectionCovers = payload
    },
    removeSelectedSectionCover: (state, { payload }: PayloadAction<number | string>) => {
      const filtered = state.selectedSectionCovers.filter(
        (cover: IFeatureContents) => cover.id !== payload,
      )
      state.selectedSectionCovers = filtered
    },
    swapSelectedPolicyPosition: (state, { payload }) => {
      const swapped = [...state.selectedPolicyContents]
      swapped[payload.to] = state.selectedPolicyContents[payload.from]
      swapped[payload.from] = state.selectedPolicyContents[payload.to]
      state.selectedPolicyContents = swapped
    },
    swapSelectedCoverPosition: (state, { payload }) => {
      const swapped = [...state.selectedSectionCovers]
      swapped[payload.to] = state.selectedSectionCovers[payload.from]
      swapped[payload.from] = state.selectedSectionCovers[payload.to]
      state.selectedSectionCovers = swapped
    },
    setImpersonation: (state, { payload }) => {
      state.impersonation = payload
    },
    /**
     * Gives the imported wordings their columns, before any of their findings have been
     * fetched. The caller has already worked out which slot each policy takes, because it
     * has to ask for that policy's findings against the same slot; deciding it twice is
     * what would let the two disagree.
     *
     * The columns go up empty on purpose: the header is readable from the policy alone, so
     * the comparison can show what it is loading rather than nothing at all.
     */
    placeImportedPolicyColumns: (
      state,
      { payload }: PayloadAction<{ policy: IPolicy; slot: number }[]>,
    ) => {
      payload.forEach(({ policy, slot }) => {
        // The caller already trims to what fits; this is the backstop that keeps a
        // multi-policy import from pushing the table past the columns it can render.
        if (slot >= ComparisonColumn.MAX_SELECTED_LENGTH) return
        const occupant = state.selectedPolicyContents[slot]
        state.selectedPolicyContents[slot] = {
          id: policy.id,
          policy,
          slot,
          // Re-uploading a wording that already has a column leaves its current findings
          // on screen while the fresh ones load, rather than blanking a working column.
          featureContents: occupant?.id === policy.id ? occupant.featureContents : [],
        }
      })
      state.importingPolicyIds = uniq([
        ...state.importingPolicyIds,
        ...payload.map(({ policy }) => policy.id),
      ])
    },
    setImportedPdfData: (state, { payload }: PayloadAction<IPolicy[] | null>) => {
      state.importedPdfData = payload
    },
  },
  extraReducers: (builder) => {
    statusCases(builder, fetchClassesAsync, 'Failed to fetch classes', (state, payload) => {
      state.productClasses = payload as IClassLine[]
      state.productClassesLoaded = true
    })
    statusCases(builder, fetchPoliciesAsync, 'Failed to fetch policies', (state, payload) => {
      state.comparisonPolicies = payload as IPolicy[]
    })
    statusCases(builder, fetchFeaturesAsync, 'Failed to fetch features', (state, payload) => {
      state.comparisonFeatures = payload as ISectionFeatures[]
    })
    statusCases(
      builder,
      fetchComparisonOptionsAsync,
      'Failed to fetch comparison options',
      (state, payload) => {
        state.comparisonOptions = payload as IComparisonOptions
      },
    )
    statusCases(
      builder,
      fetchPolicyContentByIdAsync,
      'Failed to fetch feature contents',
      (state, payload) => {
        const contents = payload as IFeatureContents
        state.selectedPolicyContents[contents.slot] = contents
      },
      // An imported column is put up before its findings are asked for, so it is this
      // request finishing — either way — that ends its loading state.
      (state, arg) => {
        const policyId = arg?.policy?.id
        if (policyId == null) return
        state.importingPolicyIds = state.importingPolicyIds.filter((id) => id !== policyId)
      },
    )
    statusCases(
      builder,
      fetchSectionContentByIdAsync,
      'Failed to fetch section contents',
      (state, payload) => {
        const contents = payload as IFeatureContents
        state.selectedSectionCovers[contents.slot] = contents
      },
    )
    statusCases(builder, saveComparisonsAsync, 'Failed to save comparison')
    statusCases(
      builder,
      getSavedComparisonsAsync,
      'Failed to fetch saved comparisons',
      (state, payload) => {
        state.savedComparisons = payload
      },
    )
    statusCases(
      builder,
      getSavedComparisonAsync,
      'Failed to fetch saved comparison',
      applySavedComparison,
    )
    statusCases(builder, removeSavedComparisonAsync, 'Failed to remove saved comparison')
    statusCases(builder, updateSavedComparisonAsync, 'Failed to update saved comparison')
    statusCases(builder, emailSavedComparisonAsync, 'Failed to email saved comparison')
    statusCases(builder, approvePolicyAsync, 'Failed to approve policy')
    statusCases(builder, fetchInsurersAsync, 'Failed to fetch insurers', (state, payload) => {
      state.tmd = payload
    })

    // Not statusCases: this one only drops out of LOADING while it is still initialising
    // the buy group options, which is not the shape the helper writes. Left exactly as it
    // was rather than quietly changing when the page stops spinning.
    builder
      .addCase(fetchBuyerGroupsAsync.pending, (state) => {
        state.comparisonStatus = Status.LOADING
      })
      .addCase(
        fetchBuyerGroupsAsync.fulfilled,
        (state, { payload }: PayloadAction<IBuyGroup[]>) => {
          // initialize buyer group on the first load
          if (state.buyGroups.length < 2) {
            const countryGroups = groupBy(payload, 'country_name')
            const options =
              countryGroups['Australia'] &&
              countryGroups['Australia'].map((bg) => ({ value: bg.id, label: bg.name }))
            state.impersonation = {
              ...state.impersonation,
              countryBuyGroupOptions: options,
            }
            state.comparisonStatus = Status.IDLE
          }
          state.buyGroups = payload
        },
      )
      .addCase(fetchBuyerGroupsAsync.rejected, (state, { error }) => {
        state.comparisonStatus = Status.ERROR
        state.comparisonError = error.message || 'Failed to fetch buyer groups'
      })
  },
})

export const {
  showFilter,
  setCompareBy,
  updateFilter,
  updateSelectionModal,
  updateApprovePolicyModal,
  selectProductClassCover,
  selectPolicyContents,
  removeSelectedPolicyCoverDetails,
  resetComparison,
  clearFilterSelections,
  updateSelectedSectionCovers,
  removeSelectedSectionCover,
  swapSelectedPolicyPosition,
  swapSelectedCoverPosition,
  setImpersonation,
  removeProductClassCover,
  removeAllSelections,
  invalidateCoverComparisonData,
  placeImportedPolicyColumns,
  setImportedPdfData,
} = comparisonSlice.actions

export default comparisonSlice.reducer
